import type { MouseEvent } from "react";

// Tiny URL-shape helpers so widgets accept either share or embed URLs.
// Keeping the transform local — no over-engineered URL parsing.

export function loomEmbedUrl(input: string): string {
    // https://www.loom.com/share/{id}        → embed
    // https://www.loom.com/embed/{id}        → already embed
    return input.replace("/share/", "/embed/");
}

export function gdocEmbedUrl(input: string): string {
    // Normalize any Google Docs URL to an iframe-editable form.
    // Inputs accepted:
    //   https://docs.google.com/document/d/{ID}/edit?usp=sharing
    //   https://docs.google.com/document/d/{ID}/edit
    //   https://docs.google.com/document/d/{ID}/preview
    // For inline editing inside the deal room, the doc must be set to
    // "Anyone with the link can edit" (writer role) — `/personalize-partnership-form`
    // sets this automatically. Falls back gracefully to read-only if the
    // viewer isn't signed into a Google account.
    try {
        const url = new URL(input);
        if (url.hostname.includes("docs.google.com")) {
            const match = url.pathname.match(/^(\/document\/d\/[^/]+)/);
            if (match) {
                return `https://docs.google.com${match[1]}/edit?usp=sharing&rm=embedded`;
            }
        }
        return input;
    } catch {
        return input;
    }
}

export function gdocShareableUrl(input: string): string {
    // Normalize any Google Docs URL to its /edit?usp=sharing form for the
    // copy-to-clipboard share link. Editor-friendly when opened directly.
    try {
        const url = new URL(input);
        if (url.hostname.includes("docs.google.com")) {
            const match = url.pathname.match(/^(\/document\/d\/[^/]+)/);
            if (match) {
                return `https://docs.google.com${match[1]}/edit?usp=sharing`;
            }
        }
        return input;
    } catch {
        return input;
    }
}

export function wistiaEmbedUrl(input: string): string {
    // Accept any of:
    //   https://bisoncommerce.wistia.com/medias/{id}
    //   https://fast.wistia.net/embed/iframe/{id}
    //   {id} alone (8-13 alphanumeric chars)
    // Returns the canonical fast.wistia.net/embed/iframe URL.
    const trimmed = input.trim();
    if (/^[a-zA-Z0-9]{8,13}$/.test(trimmed)) {
        return `https://fast.wistia.net/embed/iframe/${trimmed}`;
    }
    try {
        const url = new URL(trimmed);
        if (url.hostname.includes("wistia.com") && url.pathname.startsWith("/medias/")) {
            const id = url.pathname.replace("/medias/", "").split("/")[0];
            return `https://fast.wistia.net/embed/iframe/${id}`;
        }
        return trimmed; // already an embed URL or unknown shape
    } catch {
        return trimmed;
    }
}

export function youtubeEmbedUrl(input: string): string {
    // https://www.youtube.com/watch?v={id}   → embed
    // https://youtu.be/{id}                  → embed
    // https://www.youtube.com/embed/{id}     → already embed
    try {
        const url = new URL(input);
        if (url.hostname === "youtu.be") {
            return `https://www.youtube.com/embed${url.pathname}`;
        }
        if (url.hostname.endsWith("youtube.com") && url.pathname === "/watch") {
            const id = url.searchParams.get("v");
            return id ? `https://www.youtube.com/embed/${id}` : input;
        }
        return input;
    } catch {
        return input;
    }
}

// Click handler for external links that must open in a NEW tab even where
// the anchor's target="_blank" is not honoured (some browser extensions and
// in-app webviews strip it and navigate the room away). Plain left clicks
// open the URL from script and cancel the default navigation; modified
// clicks (ctrl / cmd / shift / middle) fall through to the browser so its
// own "open in new tab / window" behaviour still works.
//
// The default is cancelled unconditionally. With "noopener" in the feature
// string window.open returns null BY SPEC, so its return value says nothing
// about whether a tab opened; the first cut of this helper gated
// preventDefault on it and, with the target attribute stripped, opened the
// tab AND navigated the room away (caught on the live site, 2026-09-04).
// A user-initiated click is not popup-blocked in any mainstream browser.
export function openInNewTab(href: string) {
    return (e: MouseEvent<HTMLElement>) => {
        if (e.defaultPrevented || e.button !== 0) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        window.open(href, "_blank", "noopener,noreferrer");
    };
}
